import React, { Component, ErrorInfo, ReactNode } from "react";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error("ErrorBoundary caught an error:", error, errorInfo);
  }

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#f9fafb",
          padding: "20px"
        }}>
          <div style={{
            background: "white",
            padding: "40px",
            borderRadius: "16px",
            textAlign: "center",
            maxWidth: "500px",
            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.05)"
          }}>
            <div style={{
              width: "60px",
              height: "60px",
              borderRadius: "50%",
              background: "#fee2e2",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 20px"
            }}>
              <span style={{ fontSize: "24px" }}>⚠️</span>
            </div>
            <h2 style={{
              color: "#1a1a1a",
              marginBottom: "12px",
              fontFamily: "'Outfit', sans-serif",
              fontSize: "1.5rem"
            }}>
              Something went wrong
            </h2>
            <p style={{
              color: "#6b7280",
              marginBottom: "24px",
              fontSize: "0.95rem"
            }}>
              We're sorry, but something unexpected happened. Please try refreshing the page.
            </p>
            <button
              onClick={() => window.location.reload()}
              style={{
                background: "#6366f1",
                color: "white",
                border: "none",
                padding: "12px 24px",
                borderRadius: "8px",
                cursor: "pointer",
                fontWeight: "600",
                fontSize: "1rem"
              }}
            >
              Refresh Page
            </button>
            {process.env.NODE_ENV === "development" && this.state.error && (
              <details style={{
                marginTop: "24px",
                textAlign: "left",
                padding: "12px",
                background: "#fef2f2",
                borderRadius: "8px",
                fontSize: "0.8rem"
              }}>
                <summary style={{ cursor: "pointer", fontWeight: "600", marginBottom: "8px" }}>
                  Error Details
                </summary>
                <pre style={{ overflow: "auto", color: "#dc2626" }}>
                  {this.state.error.toString()}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
